import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useMediaStream — single source of truth for browser camera + microphone access
 * across `LiveBroadcaster` and `ConferenceRoom`.
 *
 * Features:
 *   - Requests permission explicitly with clear states (idle | requesting | ready | denied | error)
 *   - Enumerates cameras so the user can pick or flip front/back on mobile
 *   - Preserves the selected device through reconnects
 *   - Gracefully handles audio-only fallback (e.g. desktops without webcam)
 *   - Works in iOS Safari (requires a user gesture; hook returns an `acquire()` fn you wire to a click)
 */
export function useMediaStream({ preferFront = true } = {}) {
  const [state, setState] = useState("idle"); // idle | requesting | ready | denied | error
  const [error, setError] = useState("");
  const [devices, setDevices] = useState({ videoIn: [], audioIn: [] });
  const [current, setCurrent] = useState({ videoDeviceId: null, audioDeviceId: null, facingMode: preferFront ? "user" : "environment" });
  const streamRef = useRef(null);

  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const enumerate = useCallback(async () => {
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      setDevices({
        videoIn: list.filter((d) => d.kind === "videoinput"),
        audioIn: list.filter((d) => d.kind === "audioinput"),
      });
    } catch { /* ignore */ }
  }, []);

  const isSecure = typeof window !== "undefined" && (window.isSecureContext || window.location.hostname === "localhost");

  const acquire = useCallback(async (opts = {}) => {
    if (!navigator?.mediaDevices?.getUserMedia) {
      setState("error");
      setError("Your browser doesn't support camera access. Try Chrome, Safari, or Firefox.");
      return null;
    }
    if (!isSecure) {
      setState("error");
      setError("Camera requires a secure (HTTPS) connection.");
      return null;
    }
    // Reuse existing stream if already live
    if (streamRef.current && !opts.force) {
      setState("ready");
      return streamRef.current;
    }
    stop();
    setState("requesting");
    setError("");

    const videoDeviceId = opts.videoDeviceId ?? current.videoDeviceId;
    const audioDeviceId = opts.audioDeviceId ?? current.audioDeviceId;
    const facingMode = opts.facingMode ?? current.facingMode;
    const override = opts.videoConstraints || null;

    // Build constraints — device selection wins over facingMode (more specific).
    // `videoConstraints` (passed from caller) provides width/height/fps ideals
    // that get merged in — used by CameraSettingsPanel to switch HD ↔ 4K ↔ 60fps.
    const baseVideo = videoDeviceId
      ? {
          deviceId: { exact: videoDeviceId },
          width:  { ideal: 1920 },
          height: { ideal: 1080 },
          aspectRatio: { ideal: 16 / 9 },
          frameRate: { ideal: 30, max: 60 },
          // Advanced track constraints that engage native autofocus +
          // continuous auto-exposure / auto-white-balance on phones that
          // support them. Browsers ignore unknown keys silently so this is
          // a strict upgrade on quality with zero risk.
          advanced: [
            { focusMode: "continuous" },
            { exposureMode: "continuous" },
            { whiteBalanceMode: "continuous" },
          ],
        }
      : {
          facingMode: { ideal: facingMode },
          width:  { ideal: 1920 },
          height: { ideal: 1080 },
          aspectRatio: { ideal: 16 / 9 },
          frameRate: { ideal: 30, max: 60 },
          advanced: [
            { focusMode: "continuous" },
            { exposureMode: "continuous" },
            { whiteBalanceMode: "continuous" },
          ],
        };
    const videoConstraints = override ? { ...baseVideo, ...Object.fromEntries(Object.entries(override).filter(([, v]) => v !== undefined)) } : baseVideo;
    // Audio constraints — channelCount MUST be 1 for browsers' built-in echo
    // cancellation to engage. `channelCount: 2` was previously requested for
    // "studio quality" but it disabled AEC in Chrome/Firefox/Safari and caused
    // the loud echo viewers reported. We keep echoCancellation/noiseSuppression/
    // autoGainControl on always; the optional `announcerFx` flag adds a
    // post-capture WebAudio stage (high-pass + presence boost) for a polished
    // broadcaster voice without sacrificing AEC.
    const aec = { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1, sampleRate: 48000 };
    const audioConstraints = audioDeviceId
      ? { deviceId: { exact: audioDeviceId }, ...aec }
      : aec;

    const tryCombos = [
      { video: videoConstraints, audio: audioConstraints },
      // Fallback to 720p if 1080p is unsupported
      { video: { ...videoConstraints, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: audioConstraints },
      { video: { ...videoConstraints, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: true },
      { video: true, audio: true },
      { video: true, audio: false }, // last resort
    ];

    let stream = null;
    let lastErr = null;
    for (const c of tryCombos) {
      try {
        stream = await navigator.mediaDevices.getUserMedia(c);
        break;
      } catch (e) {
        lastErr = e;
        // OverconstrainedError / NotFoundError → try the next combo
        if (e?.name === "NotAllowedError" || e?.name === "SecurityError") break;
      }
    }

    if (!stream) {
      const name = lastErr?.name;
      if (name === "NotAllowedError" || name === "SecurityError") {
        setState("denied");
        setError("Camera / microphone permission was blocked. You can re-enable it in your browser's site settings.");
      } else if (name === "NotFoundError") {
        setState("error");
        setError("No camera found on this device.");
      } else if (name === "NotReadableError") {
        setState("error");
        setError("Your camera is already in use by another app. Close it and try again.");
      } else {
        setState("error");
        setError(lastErr?.message || "Couldn't access your camera.");
      }
      return null;
    }

    streamRef.current = stream;
    // After permission granted we can get labeled device info
    await enumerate();
    const vt = stream.getVideoTracks?.()[0];
    if (vt) {
      const settings = vt.getSettings?.() || {};
      setCurrent((prev) => ({
        ...prev,
        videoDeviceId: settings.deviceId || prev.videoDeviceId,
        facingMode: settings.facingMode || prev.facingMode,
      }));
    }
    setState("ready");
    return stream;
  }, [current.videoDeviceId, current.audioDeviceId, current.facingMode, enumerate, isSecure, stop]);

  // Detect permission changes to pre-enumerate devices on mount
  useEffect(() => {
    if (!navigator?.mediaDevices) return;
    navigator.mediaDevices.enumerateDevices().then((list) => {
      setDevices({
        videoIn: list.filter((d) => d.kind === "videoinput"),
        audioIn: list.filter((d) => d.kind === "audioinput"),
      });
    }).catch(() => {});
    const onChange = () => enumerate();
    navigator.mediaDevices.addEventListener?.("devicechange", onChange);
    return () => navigator.mediaDevices.removeEventListener?.("devicechange", onChange);
  }, [enumerate]);

  const flipCamera = useCallback(async () => {
    // Prefer toggling facingMode when on mobile; fallback to next camera in the list.
    if (devices.videoIn.length >= 2) {
      const next = devices.videoIn.find((d) => d.deviceId !== current.videoDeviceId);
      if (next) {
        await acquire({ videoDeviceId: next.deviceId, force: true });
        return;
      }
    }
    await acquire({ facingMode: current.facingMode === "user" ? "environment" : "user", videoDeviceId: null, force: true });
  }, [acquire, current.facingMode, current.videoDeviceId, devices.videoIn]);

  const selectDevice = useCallback(async ({ videoDeviceId, audioDeviceId }) => {
    await acquire({ videoDeviceId, audioDeviceId, force: true });
  }, [acquire]);

  useEffect(() => () => stop(), [stop]);

  return {
    state,
    error,
    devices,
    current,
    stream: streamRef.current,
    acquire,
    stop,
    flipCamera,
    selectDevice,
    refreshDevices: enumerate,
    isSecure,
  };
}
